(() => {
  const root = document.getElementById('pncd-root');
  if (!root) return;
  const rawBase = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || root.dataset.rootUrl || '/';
  const base = rawBase.endsWith('/') ? rawBase : rawBase + '/';
  const cartUrl = root.dataset.cartUrl || base + 'cart';
  const closeButton = root.querySelector('.pncd-close');
  const itemsElement = root.querySelector('[data-pncd-items]');
  const emptyElement = root.querySelector('[data-pncd-empty]');
  const footerElement = root.querySelector('[data-pncd-footer]');
  const statusElement = root.querySelector('[data-pncd-status]');
  let opener = null;
  let closeTimer = null;
  let busy = false;
  let currentCurrency = root.dataset.currency || 'USD';
  const element = (tag, className, textValue) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (textValue !== undefined) node.textContent = textValue;
    return node;
  };
  const money = cents => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency', currency:currentCurrency}).format(value);
    } catch (_) { return '$' + value.toFixed(2); }
  };
  const request = async (path, options = {}) => {
    const response = await fetch(base + path, {credentials:'same-origin', ...options, headers:{Accept:'application/json', ...(options.headers || {})}});
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.description || data?.message || 'Please try again or view your cart.');
    return data;
  };
  const setStatus = message => { statusElement.textContent = message || ''; };
  const syncHeaderCount = count => {
    document.querySelectorAll('.pn-cart-link').forEach(link => {
      link.setAttribute('aria-label', 'Cart (' + count + ')');
      let badge = link.querySelector('.pn-cart-count');
      if (count > 0) {
        if (!badge) { badge = element('span','pn-cart-count'); link.append(badge); }
        badge.textContent = String(count);
      } else if (badge) badge.remove();
    });
  };
  const makeAction = (label, textValue, action) => {
    const button = element('button','',textValue);
    button.type = 'button';
    button.dataset.pncdAction = action;
    button.setAttribute('aria-label',label);
    return button;
  };
  const makeItem = item => {
    const card = element('article','pncd-item');
    card.dataset.key = item.key;
    card.dataset.quantity = String(item.quantity);
    const imageLink = element('a','pncd-item__image');
    imageLink.href = item.url || cartUrl;
    imageLink.setAttribute('aria-label',item.product_title || item.title || 'View product');
    const imageUrl = item.featured_image?.url || item.image;
    if (imageUrl) {
      const image = element('img');
      image.src = imageUrl; image.alt = ''; image.loading = 'lazy'; image.width = 88; image.height = 88;
      imageLink.append(image);
    }
    card.append(imageLink);
    const body = element('div','pncd-item__body');
    const title = element('a','pncd-item__title',item.product_title || item.title || 'Product');
    title.href = item.url || cartUrl;
    body.append(title);
    if (item.variant_title && item.variant_title !== 'Default Title') body.append(element('p','pncd-item__variant',item.variant_title));
    if (item.properties && typeof item.properties === 'object') {
      Object.entries(item.properties).forEach(([name,value]) => {
        if (name.startsWith('_') || value === null || value === '') return;
        body.append(element('p','pncd-item__property',name + ': ' + String(value)));
      });
    }
    body.append(element('p','pncd-item__price',money(item.final_line_price ?? item.line_price)));
    const bottom = element('div','pncd-item__bottom');
    const qty = element('div','pncd-qty');
    qty.setAttribute('aria-label','Quantity for ' + (item.product_title || item.title));
    qty.append(
      makeAction('Decrease quantity of ' + (item.product_title || item.title),'−','decrease'),
      element('span','',String(item.quantity)),
      makeAction('Increase quantity of ' + (item.product_title || item.title),'+','increase')
    );
    bottom.append(qty,makeAction('Remove ' + (item.product_title || item.title),'Remove','remove'));
    bottom.lastElementChild.className = 'pncd-remove';
    body.append(bottom); card.append(body);
    return card;
  };
  const render = cart => {
    if (!cart || !Array.isArray(cart.items)) return;
    currentCurrency = cart.currency || currentCurrency;
    const count = Number(cart.item_count || 0);
    root.querySelector('[data-pncd-count]').textContent = String(count);
    syncHeaderCount(count);
    itemsElement.replaceChildren(...cart.items.map(makeItem));
    emptyElement.hidden = count > 0;
    footerElement.hidden = count === 0;
    itemsElement.hidden = count === 0;
    const total = money(cart.total_price);
    root.querySelector('[data-pncd-total]').textContent = total;
    root.querySelector('[data-pncd-checkout-total]').textContent = total;
    const original = Number(cart.original_total_price || 0);
    const final = Number(cart.total_price || 0);
    const savings = root.querySelector('[data-pncd-savings]');
    savings.hidden = original <= final;
    if (original > final) root.querySelector('[data-pncd-savings-value]').textContent = '−' + money(original - final);
  };
  const refresh = async () => { const cart = await request('cart.js'); render(cart); return cart; };
  const open = trigger => {
    clearTimeout(closeTimer); opener = trigger || document.activeElement; root.hidden = false;
    document.documentElement.classList.add('pncd-no-scroll');
    requestAnimationFrame(() => root.classList.add('is-open'));
    closeButton.focus();
  };
  const close = () => {
    if (root.hidden) return;
    root.classList.remove('is-open'); document.documentElement.classList.remove('pncd-no-scroll');
    closeTimer = setTimeout(() => { root.hidden = true; },300);
    if (opener && typeof opener.focus === 'function') opener.focus();
  };
  const change = async (key,quantity,actionName,cardIndex) => {
    if (busy) return;
    busy = true; setStatus('Updating cart…'); itemsElement.setAttribute('aria-busy','true');
    try {
      const cart = await request('cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:key,quantity})});
      render(cart);
      const cards = itemsElement.querySelectorAll('.pncd-item');
      const nextCard = cards[Math.min(cardIndex,cards.length - 1)];
      const nextFocus = quantity > 0 ? nextCard?.querySelector('[data-pncd-action="' + actionName + '"]') : nextCard?.querySelector('.pncd-item__title');
      (nextFocus || closeButton).focus();
      setStatus('Cart updated.');
    } catch (error) {
      setStatus(error.message || 'Could not update the cart. Please try again.');
      await refresh().catch(() => {});
    } finally { busy = false; itemsElement.removeAttribute('aria-busy'); }
  };
  document.addEventListener('click',async event => {
    if (!(event.target instanceof Element)) return;
    const closeTarget = event.target.closest('[data-pncd-close]');
    if (closeTarget && root.contains(closeTarget)) { event.preventDefault(); close(); return; }
    const action = event.target.closest('[data-pncd-action]');
    if (action && root.contains(action)) {
      const card = action.closest('.pncd-item'); if (!card) return;
      const quantity = Number(card.dataset.quantity || 0);
      const next = action.dataset.pncdAction === 'remove' ? 0 : quantity + (action.dataset.pncdAction === 'increase' ? 1 : -1);
      if (next >= 0) change(card.dataset.key,next,action.dataset.pncdAction,Array.from(itemsElement.children).indexOf(card));
      return;
    }
    const cartLink = event.target.closest('.pn-cart-link');
    if (!cartLink || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault(); setStatus('');
    try { await refresh(); open(cartLink); } catch (_) { window.location.assign(cartLink.href || cartUrl); }
  });
  document.addEventListener('submit',async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-pn-form]')) return;
    const variant = form.querySelector('[name="id"]:not(:disabled)');
    if (!variant?.value) return;
    event.preventDefault(); event.stopPropagation();
    const submit = event.submitter || form.querySelector('[type="submit"]');
    if (submit) { submit.disabled = true; submit.setAttribute('aria-busy','true'); }
    const productStatus = form.querySelector('[data-pn-status]');
    if (productStatus) productStatus.textContent = '';
    setStatus('');
    try { await request('cart/add.js',{method:'POST',body:new FormData(form)}); }
    catch (error) {
      if (productStatus) productStatus.textContent = error.message || 'Could not add this item. Please try again.';
      if (submit) { submit.disabled = !variant.value || variant.disabled; submit.removeAttribute('aria-busy'); }
      return;
    }
    try { await refresh(); open(submit); }
    catch (_) { window.location.assign(cartUrl); }
    finally { if (submit) { submit.disabled = !variant.value || variant.disabled; submit.removeAttribute('aria-busy'); } }
  },true);
  document.addEventListener('keydown',event => {
    if (root.hidden || !root.classList.contains('is-open')) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(root.querySelectorAll('a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])'))
      .filter(node => !node.closest('[hidden]') && node.getClientRects().length);
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  window.addEventListener('pageshow', () => { refresh().catch(() => {}); });
})();
